/**
 * Lookbook Rail engine — sections/lookbook-rail.liquid
 *
 * Desktop (≥1025px, ≥2 looks, motion allowed): pins the section and turns
 * vertical page scroll into horizontal travel of the rail; each look's caption
 * reveals as it enters (ScrollTrigger containerAnimation), its photo gets a
 * gentle scrub-zoom, and a progress bar tracks the run.
 * Tablet/mobile, single-look, reduced motion, no JS: the CSS native scroll-snap
 * rail is used as-is — fully visible and swipeable.
 *
 * Same idioms as heritage-story.js: idempotent, breakpoint/reduced-motion aware,
 * shopify:section load/unload safe. Plumbing in gsap-scroll.js.
 */
(function () {
  'use strict';

  if (window.__lookbookRailInit) return; // include-twice guard
  window.__lookbookRailInit = true;

  function bindMQ(mq, cb) {
    if (mq.addEventListener) { mq.addEventListener('change', cb); return function () { mq.removeEventListener('change', cb); }; }
    if (mq.addListener) { mq.addListener(cb); return function () { mq.removeListener(cb); }; }
    return function () {};
  }

  function boot(gsap, ScrollTrigger) {
    function setup(root) {
      if (root.dataset.lrInit === '1') return;
      root.dataset.lrInit = '1';

      var viewport = root.querySelector('.lr__viewport');
      var track = root.querySelector('.lr__track');
      var items = [].slice.call(root.querySelectorAll('.lr__item'));
      if (!viewport || !track || items.length === 0) return;

      var imgs = items.map(function (it) { return it.querySelector('.lr__img'); }).filter(Boolean);
      var bar = root.querySelector('.lr__progress-bar');
      var mqDesktop = window.matchMedia('(min-width: 1025px)');

      var tl = null;
      var subTriggers = [];

      function revealsOf(it) { return [].slice.call(it.querySelectorAll('.lr__reveal')); }
      function allReveals() {
        var arr = [];
        items.forEach(function (it) { arr = arr.concat(revealsOf(it)); });
        return arr;
      }

      function teardown() {
        if (tl) { if (tl.scrollTrigger) tl.scrollTrigger.kill(); tl.kill(); tl = null; }
        subTriggers.forEach(function (s) { s.kill(); });
        subTriggers = [];
        root.classList.remove('lr--pinned');
        gsap.set([track].concat(imgs).concat(allReveals()), { clearProps: 'all' });
        if (bar) bar.style.width = '';
      }

      function buildDesktop() {
        root.classList.add('lr--pinned');

        tl = gsap.to(track, {
          x: function () { return -(track.scrollWidth - viewport.clientWidth); },
          ease: 'none',
          scrollTrigger: {
            trigger: root,
            start: 'top top',
            end: function () { return '+=' + (track.scrollWidth - viewport.clientWidth); },
            scrub: 0.6,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: function (self) { if (bar) bar.style.width = (self.progress * 100) + '%'; }
          }
        });
        if (tl.scrollTrigger) subTriggers.push(tl.scrollTrigger);

        // caption reveals — never hide looks already on screen at the start
        items.forEach(function (it) {
          var rev = revealsOf(it);
          if (!rev.length) return;
          if (it.getBoundingClientRect().left < window.innerWidth * 0.92) return; // visible now
          gsap.set(rev, { opacity: 0, y: 24 });
          subTriggers.push(ScrollTrigger.create({
            trigger: it,
            containerAnimation: tl,
            start: 'left 88%',
            once: true,
            onEnter: function () {
              gsap.to(rev, { opacity: 1, y: 0, duration: 0.6, stagger: 0.06, ease: 'power2.out' });
            }
          }));
        });

        // gentle scrub-zoom for depth as each photo crosses the viewport
        imgs.forEach(function (img) {
          var tween = gsap.fromTo(img, { scale: 1.08 }, {
            scale: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: img.closest('.lr__item'),
              containerAnimation: tl,
              start: 'left right',
              end: 'right left',
              scrub: true
            }
          });
          if (tween.scrollTrigger) subTriggers.push(tween.scrollTrigger);
        });
      }

      function apply() {
        if (!root.isConnected) return;
        teardown();
        if (window.GSAPScroll.reduced()) return;              // native scroll-snap (CSS)
        if (!(mqDesktop.matches && items.length > 1)) return; // mobile/single → native rail
        buildDesktop();
        ScrollTrigger.refresh();
      }

      var unbindMQ = bindMQ(mqDesktop, apply);
      window.GSAPScroll.onReducedChange(apply);

      root._lrDestroy = function () {
        teardown();
        unbindMQ();
        root.dataset.lrInit = '';
        root._lrDestroy = null;
      };

      apply();
    }

    function scan() {
      [].slice.call(document.querySelectorAll('[data-lookbook-rail]')).forEach(setup);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scan);
    } else {
      scan();
    }

    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target.querySelector && e.target.querySelector('[data-lookbook-rail]');
      if (root) setup(root);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var root = e.target.querySelector && e.target.querySelector('[data-lookbook-rail]');
      if (root && root._lrDestroy) root._lrDestroy();
    });
  }

  if (window.GSAPScroll) {
    window.GSAPScroll.ready(boot);
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      if (window.GSAPScroll) window.GSAPScroll.ready(boot);
    });
  }
})();
